import React, { useEffect, useState, useMemo } from "react";
import {
    Box, CssBaseline, Breadcrumbs, Link, Typography, Stack, Button,
    Paper, TextField, InputAdornment, MenuItem,
    TableContainer, Table, TableHead, TableRow, TableCell,
    TableBody, TableSortLabel, CircularProgress, IconButton,
    Chip, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
    Avatar, TablePagination, Menu
} from "@mui/material";
import {
    Search, Add, Edit, DeleteOutline, WarningAmber,
    MoreVert, Phone,
    FilterList
} from '@mui/icons-material';
import { useNavigate } from "react-router-dom";
import Header from "../../component/header/Header";

const colors = {
    pageBg: '#F8F9FA',
    sidebarBg: '#F1F2F6',
    filterBg: "#FFF9E6",
    accent: '#FFC107',
    tableHeader: '#FDF7E2',
    textMain: '#5D4037',
    accentDark: '#FFA000',
    white: '#FFFFFF'
};

const StudentListScreen = () => {
    const navigation = useNavigate();

    // --- State ---
    const [studentList, setStudentList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);

    // Search & Pagination
    const [filterSearch, setFilterSearch] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('fullname');

    // UI State
    const [actionAnchorEl, setActionAnchorEl] = useState(null);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    useEffect(() => {
        getStudentList();
        localStorage.setItem("current_tab", "Student");
    }, []);

    const getStudentList = async () => {
        setLoading(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/students-with-parents`);
            const resJson = await response.json();
            if (resJson.status === 1) {
                setStudentList(resJson.data);
            } else {
                setSnackbar({ open: true, message: resJson.message, severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Connection Error", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    const deleteStudent = async (studentId) => {
        setLoadingDelete(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/students/${studentId}`, {
                method: 'DELETE'
            });
            const resJson = await response.json();
            if (resJson.status === 1) {
                setSnackbar({ open: true, message: "Student deleted successfully", severity: "success" });
                setOpenDeleteDialog(false);
                getStudentList();
            } else {
                setSnackbar({ open: true, message: resJson.message, severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Delete failed", severity: "error" });
        } finally {
            setLoadingDelete(false);
        }
    };

    // --- Table & Search Logic ---
    const handleActionOpen = (event, student) => {
        setActionAnchorEl(event.currentTarget);
        setSelectedStudent(student);
    };

    const handleActionClose = () => {
        setActionAnchorEl(null);
    };

    const handleSortRequest = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Universal Search: Filters by Student Name, Parent Name, or Parent Number
    const filteredData = useMemo(() => {
        return studentList.filter((item) => {
            const searchStr = filterSearch.toUpperCase();
            return (
                item.fullname?.toUpperCase().includes(searchStr) ||
                item.parentName?.toUpperCase().includes(searchStr) ||
                item.parentNumber?.includes(searchStr)
            );
        });
    }, [studentList, filterSearch]);

    const paginatedData = useMemo(() => {
        const sorted = [...filteredData].sort((a, b) => {
            const aValue = a[orderBy] || "";
            const bValue = b[orderBy] || "";
            return order === 'asc' ? (aValue < bValue ? -1 : 1) : (bValue < aValue ? -1 : 1);
        });
        return sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [filteredData, page, rowsPerPage, order, orderBy]);

    const columns = [
        { id: 'fullname', name: 'Student Name' },
        { id: 'gender', name: 'Gender' },
        { id: 'parentNumber', name: 'Parent Contact' },
        { id: 'amount', name: 'Fee (RM)' },
        { id: 'status', name: 'Status' }
    ];

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
            <CssBaseline />
            <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5 }}>

                <Breadcrumbs sx={{ mb: 2 }}>
                    <Link underline="hover" color="inherit" onClick={() => navigation("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
                    <Typography color="text.primary">Students</Typography>
                </Breadcrumbs>

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Header title="Student Directory" subtitle="Manage student enrollment and linked guardians" />
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigation("/Portal/Students/AddStudent")}
                        sx={{ bgcolor: colors.accent, color: colors.textMain, borderRadius: 2, fontWeight: 'bold', '&:hover': { bgcolor: colors.accentDark } }}
                    >
                        Add Student
                    </Button>
                </Stack>

                {/* Universal Search Bar */}
                <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: colors.filterBg, borderRadius: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                        <FilterList sx={{ color: colors.textMain }} />
                        <TextField
                            size="small"
                            placeholder="Search student, parent, or contact number..."
                            value={filterSearch}
                            onChange={(e) => { setFilterSearch(e.target.value); setPage(0); }}
                            sx={{ bgcolor: '#F8F9FA', borderRadius: 2, flexGrow: 1, maxWidth: 500, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                            InputProps={{ startAdornment: (<InputAdornment position="start"><Search fontSize="small" /></InputAdornment>) }}
                        />
                    </Stack>
                </Paper>

                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #F0E6CC', borderRadius: 4, maxHeight: '55vh' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.id}
                                        sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}
                                    >
                                        <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={orderBy === column.id ? order : 'asc'}
                                            onClick={() => handleSortRequest(column.id)}
                                        >
                                            {column.name}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell align="right" sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}><CircularProgress size={30} /></TableCell></TableRow>
                            ) : paginatedData.length === 0 ? (
                                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}>No student records found.</TableCell></TableRow>
                            ) : paginatedData.map((row) => (
                                <TableRow key={row.studentId} hover>
                                    <TableCell>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Avatar sx={{ bgcolor: "#c7dbe1ff", color: "#1a5465ff", fontSize: 14 }}>{row.fullname?.split(" ")[0]?.charAt(0)}{row.fullname?.split(" ")[1]?.charAt(0)}</Avatar>
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="bold">{row.fullname}</Typography>
                                                <Typography variant="caption" color="text.secondary">Parent: {row.parentName}</Typography>
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={row.gender || "-"} size="small" variant="outlined" sx={{ borderRadius: 1.5, borderColor: '#FFECB3' }} />
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Phone fontSize="small" color="action" />
                                            <Typography variant="body2">{row.parentNumber || "-"}</Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold" color="success.main">RM {row.amount || "0"}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.status === "A" ? "Active" : "Inactive"}
                                            size="small"
                                            sx={{
                                                borderRadius: 1.5,
                                                bgcolor: row.status === "A" ? '#E8F5E9' : '#FFEBEE',
                                                color: row.status === "A" ? '#2E7D32' : '#C62828',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={(e) => handleActionOpen(e, row)}><MoreVert /></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, p) => setPage(p)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Box>

            {/* --- ACTION MENU --- */}
            <Menu anchorEl={actionAnchorEl} open={Boolean(actionAnchorEl)} onClose={handleActionClose}>
                <MenuItem onClick={() => { navigation(`/Portal/Students/EditStudent/${selectedStudent?.studentId}`); handleActionClose(); }}>
                    <Edit fontSize="small" sx={{ mr: 1 }} /> Edit Student
                </MenuItem>
                <MenuItem onClick={() => { setOpenDeleteDialog(true); handleActionClose(); }} sx={{ color: 'error.main' }}>
                    <DeleteOutline fontSize="small" sx={{ mr: 1 }} /> Remove Student
                </MenuItem>
            </Menu>

            {/* --- DELETE DIALOG --- */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} PaperProps={{ sx: { borderRadius: 4 } }}>
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <WarningAmber sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                    <DialogTitle sx={{ fontWeight: 'bold', p: 0 }}>Confirm Delete</DialogTitle>
                    <DialogContent sx={{ mt: 2 }}>
                        <Typography>Are you sure you want to delete <b>{selectedStudent?.fullname}</b>?</Typography>
                        <Typography variant="caption" color="text.secondary">This action cannot be undone.</Typography>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', gap: 2, mt: 2 }}>
                        <Button onClick={() => setOpenDeleteDialog(false)} color="inherit" variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => deleteStudent(selectedStudent?.studentId)}
                            disabled={loadingDelete}
                            sx={{ borderRadius: 2, fontWeight: 'bold' }}
                        >
                            {loadingDelete ? <CircularProgress size={20} /> : "Delete Student"}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default StudentListScreen;